import { FC, PropsWithChildren } from 'react';
import { Interpolation, Theme, css } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import { breakpoints } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { featureFlags } from '../../../../feature-flags';
import { useActiveFilters } from '../../hooks/use-active-filters';
import { useCallIntelLocations } from '../../hooks/use-call-intel-locations';
import { TaskArchivalBanner } from '../../task-archival-banner';
import { FilterNotifications } from '../atoms/filter-notifications';
import { CallIntelFiltersProvider } from '../context/call-intel-filters.provider';
import { ChipBar } from '../molecules/chip-bar';
import { ContactTypeFilterChip } from '../molecules/contact-type-filter/contact-type-filter-chip';
import { ContactTypeFilterTray } from '../molecules/contact-type-filter/contact-type-filter-tray';
import { FilterTrayToggle } from '../molecules/filter-tray-section/filter-tray-toggle';
import { LocationFilterChip } from '../molecules/location-filter/location-filter-chip';
import { LocationFilterTray } from '../molecules/location-filter/location-filter-tray';
import { OfficeHoursFilterTray } from '../molecules/office-hours-filter-tray';
import { OfficeHoursResetBanner } from '../molecules/office-hours-reset-banner';
import { OfficeUserFilterChip } from '../molecules/office-user-filter/office-user-filter-chip';
import { OfficeUserFilterTray } from '../molecules/office-user-filter/office-user-filter-tray';
import { TimePeriodFilterChip } from '../molecules/time-period-filter/time-period-filter-chip';
import { TimePeriodFilterTray } from '../molecules/time-period-filter/time-period-filter-tray';
import { FilterTray } from './filter-tray';

interface CallIntelOverviewFiltersRootProps extends PropsWithChildren {
  isLoading?: boolean;
}

const CallIntelOverviewFiltersRoot: FC<CallIntelOverviewFiltersRootProps> = ({ isLoading, children }) => {
  return (
    <CallIntelFiltersProvider isLoading={isLoading}>
      <div css={styles.root}>{children}</div>
    </CallIntelFiltersProvider>
  );
};

interface CallIntelOverviewFiltersProps {
  containerStyles?: Interpolation<Theme>;
  officeUsers?: Record<string, string>;
  taskCount?: number;
  showTaskArchivalBanner?: boolean;
}

const Filters: FC<CallIntelOverviewFiltersProps> = ({
  containerStyles,
  officeUsers,
  taskCount = 0,
  showTaskArchivalBanner = false,
}) => {
  const { isMultiLocation } = useCallIntelLocations();
  const { filters } = useActiveFilters();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const hasStaffPerformanceAnalyticsAccess = useHasCallIntelAclAccess(
    [Permission.CALL_INTELLIGENCE_STAFF_PERFORMANCE_ANALYTICS],
    {
      locations: filters.locations,
      allowWeaveOverride: true,
      allowDemoOverride: true,
      requireAllLocations: true,
    }
  );

  const isOfficeHourFilterEnabled = getFeatureFlagValue(featureFlags.enableOfficeHourFilter);

  return (
    <>
      <div css={styles.filtersContainer} data-testid='call-intel-overview-filters'>
        <ChipBar maxVisibleChipsSmallMode={2} containerStyles={[styles.chipBarContents, containerStyles]}>
          {isMultiLocation && <LocationFilterChip />}
          <TimePeriodFilterChip />
          <ContactTypeFilterChip />
          {hasStaffPerformanceAnalyticsAccess && <OfficeUserFilterChip officeUsers={officeUsers} />}
        </ChipBar>
      </div>

      <FilterTray>
        {isMultiLocation && <LocationFilterTray />}
        <TimePeriodFilterTray />
        <ContactTypeFilterTray />
        {hasStaffPerformanceAnalyticsAccess && <OfficeUserFilterTray officeUsers={officeUsers} />}
        {isOfficeHourFilterEnabled && <OfficeHoursFilterTray />}
      </FilterTray>

      <FilterNotifications>
        {showTaskArchivalBanner && <TaskArchivalBanner taskCount={taskCount} />}
        <OfficeHoursResetBanner />
      </FilterNotifications>
    </>
  );
};

interface ActionsProps extends PropsWithChildren {
  showTrayFilters?: boolean;
  containerStyles?: Interpolation<Theme>;
}

const Actions: FC<ActionsProps> = ({ children, showTrayFilters = true, containerStyles }) => {
  const { isSmallMode } = useIsSmallMode();

  return (
    <div css={[styles.actionsContainer, containerStyles]} data-testid='call-intel-overview-actions'>
      {children}
      {showTrayFilters && !isSmallMode && <FilterTrayToggle css={styles.trayButton} />}
    </div>
  );
};

export const CallIntelOverviewFilters = Object.assign(CallIntelOverviewFiltersRoot, {
  Filters,
  Actions,
});

const styles = {
  root: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
    align-items: center;
  `,
  filtersContainer: css`
    display: contents;
  `,
  chipBarContents: css`
    display: contents;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      display: flex;
    }
  `,
  actionsContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    margin-left: auto;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      margin-left: 0;
    }
  `,
  trayButton: css`
    padding: ${theme.spacing(0.5)};
    height: ${theme.spacing(4)};
    width: ${theme.spacing(4)};
  `,
};
